import { FC, memo, useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { RangeFilter } from "@/components/filters/Ranges/RangeFilter";
import {
  MODEL_SCORE_PILLAR_LABELS,
  MODEL_SCORE_PILLARS,
  type ModelScorePillar,
} from "@/lib/scoring/modelScoreFields";
import { MODEL_SCORE_TIERS } from "@/lib/scoring/scoreTone";
import { trackEvent } from "@/utils/analytics";
import { getAnalyticsSegmentName } from "@/utils/analyticsContext";
import type { ScoringModel } from "@/apis/argo/scoringModels";
import { hasComposite, type ModelScoreEntry, type ScoreRange } from "./slice";

const DEBOUNCE_MS = 500;

export type UpdateEntryFn = (
  modelId: string,
  updater: (prev: ModelScoreEntry) => ModelScoreEntry
) => void;

type ScoreField = "composite" | ModelScorePillar;

const SCORE_FIELD_LABELS: Record<ScoreField, string> = {
  composite: "Total score",
  ...MODEL_SCORE_PILLAR_LABELS,
};

type ScoreRangeRowProps = {
  modelId: string;
  field: ScoreField;
  range: ScoreRange | undefined;
  onChange: (field: ScoreField, range: ScoreRange | undefined) => void;
};

/** One min/max row (total score or a pillar). Edits land in the draft
 * immediately so Confirm reflects them; only analytics is debounced. */
const ScoreRangeRow: FC<ScoreRangeRowProps> = memo(
  ({ modelId, field, range, onChange }) => {
    const handleRangeChange = useCallback(
      (min: number | undefined, max: number | undefined) =>
        onChange(
          field,
          min === undefined && max === undefined ? undefined : { min, max }
        ),
      [onChange, field]
    );

    return (
      <Box data-testid={`scoring-model-range-${field}-${modelId}`}>
        <Typography variant="caption" color="text.secondary">
          {SCORE_FIELD_LABELS[field]}
        </Typography>
        <RangeFilter
          value={{ min: range?.min, max: range?.max }}
          onChange={handleRangeChange}
          maxNumericValue={100}
          updatedOnType
        />
      </Box>
    );
  }
);

ScoreRangeRow.displayName = "ScoreRangeRow";

type ModelScoreEntryEditorProps = {
  entry: ModelScoreEntry;
  /** Catalog model backing this entry (absent when it was deleted). */
  model: ScoringModel | undefined;
  onUpdate: UpdateEntryFn;
};

/**
 * Tier / score-range criteria editor for one selected scoring model. Tier is
 * a banded composite, so tier and total score are exclusive — the mode toggle
 * clears the other on switch. Fit / Need / Intent ranges are independent of
 * the mode and ANDed with it. Argo's model list strips pillars, so all three
 * pillar rows show; a pillar the model doesn't score simply matches no company.
 */
export const ModelScoreEntryEditor: FC<ModelScoreEntryEditorProps> = memo(
  ({ entry, model, onUpdate }) => {
    const { modelId } = entry;

    const trackFilterUsed = useCallback(
      (properties: Record<string, unknown>) => {
        trackEvent("scoring_filter_used", {
          model_id: model?._id,
          model_family: modelId,
          segment: getAnalyticsSegmentName(),
          ...properties,
        });
      },
      [model, modelId]
    );

    const [mode, setMode] = useState<"tier" | "score">(
      hasComposite(entry) ? "score" : "tier"
    );

    const trackDebounceRef = useRef<
      Partial<Record<ScoreField, ReturnType<typeof setTimeout>>>
    >({});
    useEffect(
      () => () =>
        Object.values(trackDebounceRef.current).forEach((timer) =>
          clearTimeout(timer)
        ),
      []
    );

    const handleRangeChange = useCallback(
      (field: ScoreField, range: ScoreRange | undefined) => {
        onUpdate(modelId, (prev) => ({ ...prev, [field]: range }));
        // One event per settled edit; emptying both bounds clears the filter
        // — not a use, so it isn't tracked.
        clearTimeout(trackDebounceRef.current[field]);
        if (!range) return;
        trackDebounceRef.current[field] = setTimeout(() => {
          trackFilterUsed({
            filter_type: field === "composite" ? "score" : field,
            score_min: range.min,
            score_max: range.max,
          });
        }, DEBOUNCE_MS);
      },
      [onUpdate, modelId, trackFilterUsed]
    );

    const tiers = entry.tiers ?? [];

    const toggleTier = useCallback(
      (tier: string) => {
        onUpdate(modelId, (prev) => {
          const current = prev.tiers ?? [];
          const next = current.includes(tier)
            ? current.filter((t) => t !== tier)
            : [...current, tier];
          return { ...prev, tiers: next.length > 0 ? next : undefined };
        });
        // Mirrors the updater: this render's entry is what the updater received
        // as `prev`. Unchecking the last tier clears the filter — not a use, so
        // it isn't tracked.
        const current = entry.tiers ?? [];
        const next = current.includes(tier)
          ? current.filter((t) => t !== tier)
          : [...current, tier];
        if (next.length > 0) {
          trackFilterUsed({ filter_type: "tier", tiers: next });
        }
      },
      [onUpdate, modelId, entry.tiers, trackFilterUsed]
    );

    const handleModeChange = useCallback(
      (next: "tier" | "score" | null) => {
        if (!next || next === mode) return;
        setMode(next);
        // Switching mode clears the other dimension; pillars are kept.
        if (next === "tier") {
          onUpdate(modelId, (prev) => ({ ...prev, composite: undefined }));
        } else {
          onUpdate(modelId, (prev) => ({ ...prev, tiers: undefined }));
        }
      },
      [mode, onUpdate, modelId]
    );

    return (
      <Box
        data-testid={`scoring-model-entry-${modelId}`}
        sx={{ display: "flex", flexDirection: "column", gap: 1 }}
      >
        <Typography variant="subtitle2">{model?.name ?? modelId}</Typography>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_, next) => handleModeChange(next)}
        >
          <ToggleButton
            value="tier"
            data-testid={`scoring-model-mode-tier-btn-${modelId}`}
            sx={{ textTransform: "none", px: 1.5 }}
          >
            Tier
          </ToggleButton>
          <ToggleButton
            value="score"
            data-testid={`scoring-model-mode-score-btn-${modelId}`}
            sx={{ textTransform: "none", px: 1.5 }}
          >
            Score range
          </ToggleButton>
        </ToggleButtonGroup>

        {mode === "score" ? (
          <ScoreRangeRow
            modelId={modelId}
            field="composite"
            range={entry.composite}
            onChange={handleRangeChange}
          />
        ) : (
          <FormGroup row>
            {MODEL_SCORE_TIERS.map((tier) => (
              <FormControlLabel
                key={tier}
                control={
                  <Checkbox
                    size="small"
                    checked={tiers.includes(tier)}
                    onChange={() => toggleTier(tier)}
                    data-testid={`scoring-model-tier-checkbox-btn-${modelId}-${tier}`}
                  />
                }
                label={tier}
              />
            ))}
          </FormGroup>
        )}

        {MODEL_SCORE_PILLARS.map((pillar) => (
          <ScoreRangeRow
            key={pillar}
            modelId={modelId}
            field={pillar}
            range={entry[pillar]}
            onChange={handleRangeChange}
          />
        ))}
      </Box>
    );
  }
);

ModelScoreEntryEditor.displayName = "ModelScoreEntryEditor";
